// partita.ts — il motore della partita 1v1: regole pure, senza rete e senza orologio.
//
// Il server è l'unico arbitro: riceve dai telefoni cosa vedono (risate rilevate, «Ha riso!», volto presente o no,
// collegato o no) e decide fasi, orari e risultato. L'ora è sempre passata dall'esterno (`adesso`, ora del server
// in ms), così ogni regola si prova con test deterministici. Chi usa il motore chiama `scadenza()` per sapere
// quando richiamare `tick()`.
//
// Fasi:  attesa → conto → gioco → (verifica) → risultato → (rivincita / round successivo) → conto …
//        gioco ⇄ accusa (l'accusato conferma o contesta) · gioco/conto ⇄ pausa (volto assente, scollegato)
//        qualunque fase → chiusa (uscita) · pausa troppo lunga → risultato (sconfitta tecnica o annullata)
//
// Regole della risata (specifica §5):
//  - ogni telefono analizza sé stesso E l'avversario: due segnalazioni sulla stessa persona da telefoni diversi
//    entro `finestraConcordanzaMs` = risata confermata (analisi concordi);
//  - una segnalazione sola, o il pulsante «Ha riso!» dell'avversario = accusa: l'accusato conferma o contesta
//    (senza risposta entro `accusaMs` vale come confermata); troppe contestazioni = partita annullata;
//  - dopo la prima risata confermata si aspetta `finestraPariMs`: se ride anche l'altro, round ripetuto;
//  - volto assente per `voltoAssentePausaMs` → pausa; per `voltoAssenteSconfittaMs` → sconfitta tecnica;
//  - scollegato → pausa; non torna entro `riconnessioneMs` → partita annullata (non conta);
//  - durata massima del round raggiunta → nessun vincitore.

export type Ruolo = "a" | "b";
export const RUOLI: readonly Ruolo[] = ["a", "b"];
export const altro = (r: Ruolo): Ruolo => (r === "a" ? "b" : "a");

export type Fase = "attesa" | "conto" | "gioco" | "verifica" | "accusa" | "pausa" | "risultato" | "chiusa";
export type TipoSerie = "singola" | "meglio3";
export type MotivoEsito =
  | "risata" // l'altro ha riso
  | "tecnica_volto" // volto assente troppo a lungo
  | "tempo" // durata massima, nessuno ha riso
  | "scollegato" // annullata: qualcuno non è tornato
  | "contestazioni" // annullata: troppe contestazioni
  | "uscita"; // annullata: qualcuno è uscito
export type OrigineAccusa = "avversario" | "analisi";

export interface ParametriPartita {
  contoMs: number;
  ripresaContoMs: number;
  durataMassimaMs: number;
  finestraConcordanzaMs: number;
  finestraPariMs: number;
  accusaMs: number;
  contestazioniMassime: number;
  voltoAssentePausaMs: number;
  voltoAssenteSconfittaMs: number;
  riconnessioneMs: number;
  /** Quanto prima del VIA (o dopo «adesso») può essere datata una segnalazione: oltre si scarta. */
  tolleranzaOrarioMs: number;
}

interface StatoGiocatore {
  nome: string;
  collegato: boolean;
  pronto: boolean;
  volto: boolean;
  voltoAssenteDa: number | null;
  scollegatoDa: number | null;
  /** Contestazioni di risate viste dal SUO telefono (ha negato quello che il suo telefono ha visto). */
  contestazioni: number;
  /** Accuse fatte da lui (pulsante o analisi del suo telefono sull'altro) che l'accusato ha respinto. */
  accuseRespinte: number;
  /** Cresce a ogni (ri)collegamento: i due telefoni rifanno la videochiamata quando cambia. */
  sessione: number;
}

interface Sospetto {
  soggetto: Ruolo;
  da: Ruolo;
  t: number;
  arrivatoAlle: number;
}

interface Accusa {
  contro: Ruolo;
  origine: OrigineAccusa;
  /** Chi l'ha provocata: chi ha premuto «Ha riso!», o il telefono la cui analisi era da sola. */
  da: Ruolo;
  t: number; // quando avrebbe riso
  scade: number;
}

interface Pausa {
  motivo: "scollegato" | "volto";
  chi: Ruolo;
  scade: number;
  faseDaRiprendere: "gioco" | "attesa";
}

export interface Esito {
  vincitore: Ruolo | null;
  motivo: MotivoEsito;
  /** Ruolo di chi ha riso (o ha perso), se c'è. */
  perdente: Ruolo | null;
  round: number;
  serieFinita: boolean;
  annullata: boolean;
  /** Annullata perché questo giocatore si è scollegato e non è tornato (nel torneo perde l'incontro). */
  abbandono?: Ruolo;
}

export interface Proposta {
  da: Ruolo;
  tipo: TipoSerie;
}

/** Quello che ogni telefono riceve: la stessa partita vista da un giocatore. */
export interface VistaPartita {
  versione: number;
  fase: Fase;
  io: Ruolo;
  nomi: Record<Ruolo, string>;
  collegati: Record<Ruolo, boolean>;
  /** Contatore dei collegamenti di ciascuno: la videochiamata si rifà quando cambia (su entrambi i telefoni). */
  sessioni: Record<Ruolo, number>;
  pronti: Record<Ruolo, boolean>;
  volti: Record<Ruolo, boolean>;
  /** Consenso alla clip della risata di ciascuno (v008). */
  clip: Record<Ruolo, boolean>;
  /** v037: sì in più a mettere online il video in cui si vede (link /v/<codice>, «Riesci a non ridere?»). */
  online: Record<Ruolo, boolean>;
  /** v045: sì a «video per noi» (il video si salva per i nostri social e il sito solo se lo dicono tutti e due). */
  noi: Record<Ruolo, boolean>;
  /** v040: sì alle foto della partita in cui si vede. */
  foto: Record<Ruolo, boolean>;
  /** v038b: chi ha spento l'audio (se uno dei due lo spegne, nessuno dei due sente l'altro). */
  muti: Record<Ruolo, boolean>;
  round: number;
  serie: { numero: number; tipo: TipoSerie; vittorie: Record<Ruolo, number>; obiettivo: number };
  tVia: number | null;
  /** Tempo giocato = tempoGiocatoMs + (adesso − orologioDa) se orologioDa non è null. */
  tempoGiocatoMs: number;
  orologioDa: number | null;
  durataMassimaMs: number;
  accusa: { contro: Ruolo; origine: OrigineAccusa; scade: number } | null;
  pausa: { motivo: "scollegato" | "volto"; chi: Ruolo; scade: number } | null;
  verifica: { chi: Ruolo; scade: number } | null;
  esito: Esito | null;
  proposta: Proposta | null;
}

/** Partita conclusa, da salvare. */
export interface PartitaConclusa {
  esito: Esito;
  /** Numero della serie in questa stanza: rivincite e round di «al meglio di 3» hanno la stessa serie. */
  serie: number;
  tipoSerie: TipoSerie;
  inizio: number;
  fine: number;
  tempoGiocatoMs: number;
}

const VITTORIE_PER_TIPO: Record<TipoSerie, number> = { singola: 1, meglio3: 2 };

const giocatoreNuovo = (nome: string): StatoGiocatore => ({
  nome,
  collegato: false,
  pronto: false,
  volto: true,
  voltoAssenteDa: null,
  scollegatoDa: null,
  contestazioni: 0,
  accuseRespinte: 0,
  sessione: 0,
});

export class MotorePartita {
  private fase: Fase = "attesa";
  private versione = 1;
  private g: Record<Ruolo, StatoGiocatore>;
  private round = 0;
  private tipoSerie: TipoSerie = "singola";
  private vittorie: Record<Ruolo, number> = { a: 0, b: 0 };
  private tVia: number | null = null;
  private tempoGiocatoMs = 0; // nel round, escluse le pause (fino a quando l'orologio si è fermato)
  private orologioDa: number | null = null; // l'orologio del round corre da quest'ora (null = fermo)
  private inizioRound: number | null = null;
  private numeroSerie = 0;
  private sospetti: Sospetto[] = [];
  private risate: Partial<Record<Ruolo, number>> = {};
  private verifica: { chi: Ruolo; scade: number } | null = null;
  private accusa: Accusa | null = null;
  private pausa: Pausa | null = null;
  private esito: Esito | null = null;
  private proposta: Proposta | null = null;
  private concluse: PartitaConclusa[] = [];
  private consensiClip: Record<Ruolo, boolean> = { a: false, b: false };
  private consensiOnline: Record<Ruolo, boolean> = { a: false, b: false };
  private consensiNoi: Record<Ruolo, boolean> = { a: false, b: false };
  private consensiFoto: Record<Ruolo, boolean> = { a: false, b: false };
  private muti: Record<Ruolo, boolean> = { a: false, b: false };

  private readonly p: ParametriPartita;

  constructor(p: ParametriPartita, nomi: Record<Ruolo, string>) {
    this.p = p;
    this.g = { a: giocatoreNuovo(nomi.a), b: giocatoreNuovo(nomi.b) };
  }

  // --- lettura -----------------------------------------------------------------

  get faseAttuale(): Fase {
    return this.fase;
  }

  get versioneAttuale(): number {
    return this.versione;
  }

  vista(io: Ruolo): VistaPartita {
    const per = <T>(f: (s: StatoGiocatore) => T): Record<Ruolo, T> => ({ a: f(this.g.a), b: f(this.g.b) });
    return {
      versione: this.versione,
      fase: this.fase,
      io,
      nomi: per((s) => s.nome),
      collegati: per((s) => s.collegato),
      sessioni: per((s) => s.sessione),
      pronti: per((s) => s.pronto),
      volti: per((s) => s.volto),
      clip: { ...this.consensiClip },
      online: { ...this.consensiOnline },
      noi: { ...this.consensiNoi },
      foto: { ...this.consensiFoto },
      muti: { ...this.muti },
      round: this.round,
      serie: { numero: this.numeroSerie, tipo: this.tipoSerie, vittorie: { ...this.vittorie }, obiettivo: VITTORIE_PER_TIPO[this.tipoSerie] },
      tVia: this.tVia,
      tempoGiocatoMs: this.tempoGiocatoMs,
      orologioDa: this.orologioDa,
      durataMassimaMs: this.p.durataMassimaMs,
      accusa: this.accusa ? { contro: this.accusa.contro, origine: this.accusa.origine, scade: this.accusa.scade } : null,
      pausa: this.pausa ? { motivo: this.pausa.motivo, chi: this.pausa.chi, scade: this.pausa.scade } : null,
      verifica: this.verifica ? { ...this.verifica } : null,
      esito: this.esito ? { ...this.esito } : null,
      proposta: this.proposta ? { ...this.proposta } : null,
    };
  }

  /** Partite finite dall'ultima chiamata (da salvare). */
  prendiConcluse(): PartitaConclusa[] {
    const c = this.concluse;
    this.concluse = [];
    return c;
  }

  /** Il prossimo istante in cui il motore deve essere richiamato con tick(), o null. */
  scadenza(): number | null {
    const candidati: number[] = [];
    if (this.fase === "conto" && this.tVia !== null) candidati.push(this.tVia);
    if (this.fase === "gioco" && this.orologioDa !== null) {
      candidati.push(this.orologioDa + (this.p.durataMassimaMs - this.tempoGiocatoMs));
      for (const s of this.sospetti) candidati.push(s.arrivatoAlle + this.p.finestraConcordanzaMs);
      for (const r of RUOLI) {
        const da = this.g[r].voltoAssenteDa;
        if (da !== null) candidati.push(da + this.p.voltoAssentePausaMs);
      }
    }
    if (this.fase === "verifica" && this.verifica) candidati.push(this.verifica.scade);
    if (this.fase === "accusa" && this.accusa) candidati.push(this.accusa.scade);
    if (this.fase === "pausa" && this.pausa) candidati.push(this.pausa.scade);
    return candidati.length ? Math.min(...candidati) : null;
  }

  // --- eventi dai giocatori ---------------------------------------------------------

  collegato(r: Ruolo, adesso: number, nome?: string): void {
    const s = this.g[r];
    if (nome) s.nome = nome;
    s.collegato = true;
    s.scollegatoDa = null;
    s.sessione++;
    if (this.fase === "pausa") this.riprendi(adesso);
    this.cambiato();
  }

  scollegato(r: Ruolo, adesso: number): void {
    const s = this.g[r];
    if (!s.collegato) return;
    s.collegato = false;
    s.scollegatoDa = adesso;
    s.pronto = this.fase === "attesa" || this.fase === "risultato" ? false : s.pronto;
    // Pausa solo mentre si gioca: una risata già confermata (verifica) o un'accusa aperta si risolvono comunque
    // (scollegarsi non deve cancellare una risata); al ritorno in gioco, se manca ancora, scatta la pausa.
    if (this.fase === "gioco" || this.fase === "conto") this.mettiInPausa("scollegato", r, adesso, adesso + this.p.riconnessioneMs);
    else if (this.fase === "pausa") this.riprendi(adesso);
    this.cambiato();
  }

  pronto(r: Ruolo, adesso: number, valore = true): void {
    if (this.fase !== "attesa" && this.fase !== "risultato") return;
    if (this.fase === "risultato" && (!this.esito || this.esito.serieFinita)) return; // dopo la serie: rivincita
    this.g[r].pronto = valore;
    if (RUOLI.every((x) => this.g[x].pronto && this.g[x].collegato)) this.nuovoRound(adesso);
    this.cambiato();
  }

  /** Il telefono di `da` ha visto ridere `soggetto` all'ora `t` (ora del server). */
  segnalaRisata(da: Ruolo, soggetto: Ruolo, t: number, adesso: number): void {
    if (this.fase !== "gioco" && this.fase !== "verifica") return;
    if (this.tVia === null) return;
    if (t < this.tVia - this.p.tolleranzaOrarioMs || t > adesso + this.p.tolleranzaOrarioMs) return; // orario impossibile
    const tt = Math.min(Math.max(t, this.tVia), adesso);
    if (this.risate[soggetto] !== undefined) return;

    if (this.fase === "verifica") {
      // Si aspetta solo di sapere se ha riso anche l'altro. Vale SOLO il suo telefono (ammette di aver riso):
      // il telefono di chi ha già riso non può inventare una risata dell'altro per strappare il pari.
      const prima = this.verifica ? this.risate[this.verifica.chi] : undefined;
      if (soggetto !== this.verifica?.chi && da === soggetto && prima !== undefined && Math.abs(tt - prima) <= this.p.finestraPariMs) {
        this.risate[soggetto] = tt;
        this.cambiato();
      }
      return;
    }

    const gemello = this.sospetti.find(
      (s) => s.soggetto === soggetto && s.da !== da && Math.abs(s.t - tt) <= this.p.finestraConcordanzaMs,
    );
    if (gemello) {
      this.sospetti = this.sospetti.filter((s) => s.soggetto !== soggetto);
      this.confermaRisata(soggetto, Math.min(gemello.t, tt), adesso);
      return;
    }
    // Una sola segnalazione per telefono e soggetto: si tiene la più recente.
    this.sospetti = this.sospetti.filter((s) => !(s.soggetto === soggetto && s.da === da));
    this.sospetti.push({ soggetto, da, t: tt, arrivatoAlle: adesso });
    this.cambiato();
  }

  /** Pulsante «Ha riso!»: `da` accusa l'avversario. */
  accusaAvversario(da: Ruolo, adesso: number): void {
    if (this.fase !== "gioco") return;
    this.apriAccusa(altro(da), "avversario", da, adesso, adesso);
  }

  /** L'accusato risponde: conferma (ho riso) o contesta. */
  rispondiAccusa(r: Ruolo, conferma: boolean, adesso: number): void {
    if (this.fase !== "accusa" || !this.accusa || this.accusa.contro !== r) return;
    const a = this.accusa;
    this.accusa = null;
    if (conferma) {
      this.confermaRisata(r, a.t, adesso);
      return;
    }
    if (a.da === r) {
      // Nega quello che ha visto il SUO telefono: oltre il limite la partita è annullata.
      this.g[r].contestazioni++;
      if (this.g[r].contestazioni > this.p.contestazioniMassime) {
        this.chiudiRound({ vincitore: null, perdente: null, motivo: "contestazioni", annullata: true }, adesso);
        return;
      }
    } else {
      // Accusa dell'altro respinta: conta a CHI ACCUSA. Oltre il limite le sue accuse non valgono più
      // (decidono solo le analisi concordi): accusare a vuoto non costa nulla all'accusato.
      this.g[a.da].accuseRespinte++;
    }
    // Contestata: si riprende con un breve conto alla rovescia (le segnalazioni vecchie si dimenticano).
    this.sospetti = [];
    this.avviaConto(adesso, this.p.ripresaContoMs);
    this.cambiato();
  }

  volto(r: Ruolo, presente: boolean, adesso: number): void {
    const s = this.g[r];
    if (s.volto === presente) return;
    s.volto = presente;
    s.voltoAssenteDa = presente ? null : adesso;
    if (presente && this.fase === "pausa") this.riprendi(adesso);
    this.cambiato();
  }

  /** Rivincita o «al meglio di 3»: si propone dopo il risultato; l'altro accetta. */
  /** Consenso alla clip della risata (si può cambiare in ogni momento; il sì vale dal round dopo, il no subito: la clip in corso si butta). */
  consensoClip(r: Ruolo, valore: boolean): void {
    if (this.consensiClip[r] === valore) return;
    this.consensiClip[r] = valore;
    this.cambiato();
  }

  /** v038b: audio spento o riacceso da uno dei due. */
  muto(r: Ruolo, valore: boolean): void {
    if (this.muti[r] === valore) return;
    this.muti[r] = valore;
    this.cambiato();
  }

  /** v037: sì (o no) a mettere online il video in cui si vede. Si può cambiare in ogni momento. */
  consensoOnline(r: Ruolo, valore: boolean): void {
    if (this.consensiOnline[r] === valore) return;
    this.consensiOnline[r] = valore;
    this.cambiato();
  }

  /** v045: sì (o no) a «video per noi». Si può cambiare in ogni momento; restituisce se è cambiato (va salvato). */
  consensoNoi(r: Ruolo, valore: boolean): boolean {
    if (this.consensiNoi[r] === valore) return false;
    this.consensiNoi[r] = valore;
    this.cambiato();
    return true;
  }

  /** v040: sì (o no) alle foto della partita in cui si vede. Si può cambiare in ogni momento. */
  consensoFoto(r: Ruolo, valore: boolean): void {
    if (this.consensiFoto[r] === valore) return;
    this.consensiFoto[r] = valore;
    this.cambiato();
  }

  proponi(da: Ruolo, tipo: TipoSerie): void {
    if (this.fase !== "risultato" || !this.esito?.serieFinita) return; // a serie in corso si gioca il round dopo
    this.proposta = { da, tipo };
    this.cambiato();
  }

  accetta(r: Ruolo, adesso: number): void {
    if (this.fase !== "risultato" || !this.esito?.serieFinita || !this.proposta || this.proposta.da === r) return;
    if (!RUOLI.every((x) => this.g[x].collegato)) return;
    this.tipoSerie = this.proposta.tipo;
    this.vittorie = { a: 0, b: 0 };
    this.round = 0;
    this.proposta = null;
    this.nuovoRound(adesso);
    this.cambiato();
  }

  rifiuta(r: Ruolo): void {
    if (!this.proposta || this.proposta.da === r) return;
    this.proposta = null;
    this.cambiato();
  }

  /** Uscita volontaria: la partita in corso è annullata (uscire deve essere sempre possibile, senza penalità). */
  esci(r: Ruolo, adesso: number): void {
    if (this.fase === "chiusa") return;
    if (this.inPartita()) {
      this.chiudiRound({ vincitore: null, perdente: null, motivo: "uscita", annullata: true }, adesso);
    }
    this.g[r].collegato = false;
    this.fase = "chiusa";
    this.cambiato();
  }

  // --- tempo ------------------------------------------------------------------

  tick(adesso: number): void {
    // Più scadenze possono essere passate insieme: si risolvono in ordine finché non resta niente da fare.
    for (let giri = 0; giri < 10; giri++) {
      if (!this.passo(adesso)) return;
      this.cambiato();
    }
  }

  private passo(adesso: number): boolean {
    switch (this.fase) {
      case "conto":
        if (this.tVia !== null && adesso >= this.tVia) {
          const assente = RUOLI.find((r) => !this.g[r].collegato);
          if (assente) {
            this.mettiInPausa("scollegato", assente, adesso, (this.g[assente].scollegatoDa ?? adesso) + this.p.riconnessioneMs);
            return true;
          }
          this.fase = "gioco";
          this.orologioDa = this.tVia;
          this.inizioRound ??= this.tVia;
          // L'assenza del volto conta da quando si gioca, non da prima (es. in attesa con il viso fuori).
          for (const r of RUOLI) {
            const da = this.g[r].voltoAssenteDa;
            if (da !== null) this.g[r].voltoAssenteDa = Math.max(da, this.tVia);
          }
          return true;
        }
        return false;
      case "gioco": {
        if (this.orologioDa === null) return false;
        const assente = RUOLI.find((r) => !this.g[r].collegato);
        if (assente) {
          // Uscito mentre si risolveva una risata o un'accusa: ora che si gioca, pausa.
          this.mettiInPausa("scollegato", assente, adesso, (this.g[assente].scollegatoDa ?? adesso) + this.p.riconnessioneMs);
          return true;
        }
        const giocato = this.tempoGiocatoMs + (adesso - this.orologioDa);
        if (giocato >= this.p.durataMassimaMs) {
          this.fermaOrologio(adesso);
          this.chiudiRound({ vincitore: null, perdente: null, motivo: "tempo", annullata: false }, adesso);
          return true;
        }
        for (const r of RUOLI) {
          const da = this.g[r].voltoAssenteDa;
          if (da !== null && adesso - da >= this.p.voltoAssentePausaMs) {
            this.mettiInPausa("volto", r, adesso, da + this.p.voltoAssenteSconfittaMs);
            return true;
          }
        }
        const scaduto = this.sospetti.find((s) => adesso >= s.arrivatoAlle + this.p.finestraConcordanzaMs);
        if (scaduto) {
          this.sospetti = this.sospetti.filter((s) => s !== scaduto && s.soggetto !== scaduto.soggetto);
          this.apriAccusa(scaduto.soggetto, "analisi", scaduto.da, scaduto.t, adesso);
          return true;
        }
        return false;
      }
      case "verifica": {
        if (!this.verifica || adesso < this.verifica.scade) return false;
        const chi = this.verifica.chi;
        this.verifica = null;
        const tChi = this.risate[chi]!;
        const tAltro = this.risate[altro(chi)];
        if (tAltro !== undefined && Math.abs(tAltro - tChi) <= this.p.finestraPariMs) {
          // Pari: round ripetuto, il punteggio (e la serie) non cambia.
          this.nuovoRound(adesso, true);
          return true;
        }
        this.chiudiRound({ vincitore: altro(chi), perdente: chi, motivo: "risata", annullata: false }, adesso);
        return true;
      }
      case "accusa":
        if (this.accusa && adesso >= this.accusa.scade) {
          // Nessuna risposta: vale come confermata.
          const a = this.accusa;
          this.accusa = null;
          this.confermaRisata(a.contro, a.t, adesso);
          return true;
        }
        return false;
      case "pausa":
        if (this.pausa && adesso >= this.pausa.scade) {
          const pa = this.pausa;
          this.pausa = null;
          if (pa.motivo === "volto") {
            this.chiudiRound({ vincitore: altro(pa.chi), perdente: pa.chi, motivo: "tecnica_volto", annullata: false }, adesso);
          } else {
            this.chiudiRound({ vincitore: null, perdente: null, motivo: "scollegato", annullata: true, abbandono: pa.chi }, adesso);
          }
          return true;
        }
        return false;
      default:
        return false;
    }
  }

  // --- interni ----------------------------------------------------------------

  private inPartita(): boolean {
    return ["conto", "gioco", "verifica", "accusa", "pausa"].includes(this.fase);
  }

  private cambiato(): void {
    this.versione++;
  }

  /** `ripetizione`: lo stesso round si rigioca (pari): niente numero nuovo, niente serie nuova. */
  private nuovoRound(adesso: number, ripetizione = false): void {
    if (!ripetizione) this.round++;
    this.risate = {};
    this.sospetti = [];
    this.accusa = null;
    this.verifica = null;
    this.esito = null;
    this.tempoGiocatoMs = 0;
    this.orologioDa = null;
    this.inizioRound = null;
    const nuovaSerie = this.round === 1 && !ripetizione;
    if (nuovaSerie) this.numeroSerie++;
    for (const r of RUOLI) {
      this.g[r].pronto = false;
      if (nuovaSerie) {
        this.g[r].contestazioni = 0;
        this.g[r].accuseRespinte = 0;
      }
    }
    this.avviaConto(adesso, this.p.contoMs);
  }

  private avviaConto(adesso: number, durata: number): void {
    this.fase = "conto";
    this.tVia = adesso + durata;
  }

  /** Accumula il tempo giocato fino ad `adesso` e ferma l'orologio del round (chiamarla due volte non conta doppio). */
  private fermaOrologio(adesso: number): void {
    if (this.orologioDa !== null) {
      this.tempoGiocatoMs = Math.min(this.p.durataMassimaMs, this.tempoGiocatoMs + Math.max(0, adesso - this.orologioDa));
      this.orologioDa = null;
    }
  }

  private confermaRisata(r: Ruolo, t: number, adesso: number): void {
    this.fermaOrologio(adesso);
    this.risate[r] = t;
    this.sospetti = this.sospetti.filter((s) => s.soggetto !== r);
    this.fase = "verifica";
    this.verifica = { chi: r, scade: adesso + this.p.finestraPariMs };
    this.cambiato();
  }

  private apriAccusa(contro: Ruolo, origine: OrigineAccusa, da: Ruolo, t: number, adesso: number): void {
    if (this.risate[contro] !== undefined) return;
    // Chi ha già visto respingere troppe accuse non può più fermare il gioco con altre accuse.
    if (da !== contro && this.g[da].accuseRespinte > this.p.contestazioniMassime) return;
    this.fermaOrologio(adesso);
    this.fase = "accusa";
    this.accusa = { contro, origine, da, t, scade: adesso + this.p.accusaMs };
    this.cambiato();
  }

  private mettiInPausa(motivo: "scollegato" | "volto", chi: Ruolo, adesso: number, scade: number): void {
    if (this.fase === "pausa") {
      // Già in pausa: vale la scadenza più vicina (es. scollegato mentre era senza volto).
      if (this.pausa && scade < this.pausa.scade) this.pausa = { ...this.pausa, motivo, chi, scade };
      return;
    }
    this.fermaOrologio(adesso);
    this.accusa = null;
    this.verifica = null;
    this.sospetti = [];
    this.pausa = { motivo, chi, scade, faseDaRiprendere: "gioco" };
    this.fase = "pausa";
  }

  /**
   * In pausa, a ogni cambiamento: se manca ancora qualcuno (collegamento o volto) la pausa si aggiorna su di lui
   * (con la SUA scadenza); se ci sono tutti, si riprende con un breve conto alla rovescia.
   */
  private riprendi(adesso: number): void {
    if (this.fase !== "pausa") return;
    const scollegato = RUOLI.find((r) => !this.g[r].collegato);
    if (scollegato) {
      this.pausa = { motivo: "scollegato", chi: scollegato, scade: (this.g[scollegato].scollegatoDa ?? adesso) + this.p.riconnessioneMs, faseDaRiprendere: "gioco" };
      return;
    }
    const senzaVolto = RUOLI.find((r) => !this.g[r].volto);
    if (senzaVolto) {
      this.pausa = { motivo: "volto", chi: senzaVolto, scade: (this.g[senzaVolto].voltoAssenteDa ?? adesso) + this.p.voltoAssenteSconfittaMs, faseDaRiprendere: "gioco" };
      return;
    }
    this.pausa = null;
    for (const r of RUOLI) this.g[r].voltoAssenteDa = null;
    this.risate = {};
    this.sospetti = [];
    this.avviaConto(adesso, this.p.ripresaContoMs);
  }

  private chiudiRound(e: Omit<Esito, "round" | "serieFinita">, adesso: number): void {
    this.fermaOrologio(adesso);
    if (e.vincitore) this.vittorie[e.vincitore]++;
    const obiettivo = VITTORIE_PER_TIPO[this.tipoSerie];
    const serieFinita = e.annullata || RUOLI.some((r) => this.vittorie[r] >= obiettivo) || this.tipoSerie === "singola";
    this.esito = { ...e, round: this.round, serieFinita };
    this.fase = "risultato";
    this.accusa = null;
    this.verifica = null;
    this.pausa = null;
    this.sospetti = [];
    this.proposta = null;
    for (const r of RUOLI) this.g[r].pronto = false;
    this.concluse.push({
      esito: { ...this.esito },
      serie: this.numeroSerie,
      tipoSerie: this.tipoSerie,
      inizio: this.inizioRound ?? adesso,
      fine: adesso,
      tempoGiocatoMs: this.tempoGiocatoMs,
    });
    this.cambiato();
  }
}
